import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Clock,
  MapPin,
  User,
  Phone,
  Mail,
  Check,
  X,
  DollarSign,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Send,
  FileText,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [confirmingQuote, setConfirmingQuote] = useState(null);
  const [quotePrice, setQuotePrice] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("adminToken");
    return {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }, []);

  const fetchQuotes = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
      });

      const response = await fetch(`${API_URL}/api/admin/quotes?${params}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setQuotes(data.quotes || []);
        setTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      console.error("Error fetching quotes:", error);
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }, [page, getAuthHeaders]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const filteredQuotes = quotes.filter(
    (quote) =>
      quote.booking_id?.toLowerCase().includes(search.toLowerCase()) ||
      quote.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
      quote.customer_email?.toLowerCase().includes(search.toLowerCase()) ||
      quote.pickup_location?.toLowerCase().includes(search.toLowerCase()) ||
      quote.dropoff_location?.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenConfirm = (quote) => {
    setConfirmingQuote(quote);
    setQuotePrice("");
    setAdminNotes("");
    setSendEmail(true);
  };

  const handleConfirmQuote = async () => {
    if (!confirmingQuote || !quotePrice) {
      toast.error("Please enter a price");
      return;
    }

    const price = parseFloat(quotePrice);
    if (isNaN(price) || price <= 0) {
      toast.error("Please enter a valid price greater than 0");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/quotes/${confirmingQuote.booking_id}/confirm`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            price: price,
            send_email: sendEmail,
            admin_notes: adminNotes || null,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(
          data.email_sent
            ? "Quote confirmed and email sent!"
            : "Quote confirmed successfully"
        );
        setConfirmingQuote(null);
        fetchQuotes();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to confirm quote");
      }
    } catch (error) {
      console.error("Error confirming quote:", error);
      toast.error("Error confirming quote");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectQuote = async (bookingId) => {
    if (!window.confirm("Are you sure you want to reject this quote request?")) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/admin/quotes/${bookingId}/reject`,
        {
          method: "POST",
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        toast.success("Quote rejected");
        fetchQuotes();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to reject quote");
      }
    } catch (error) {
      console.error("Error rejecting quote:", error);
      toast.error("Error rejecting quote");
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            Pending Quote
          </span>
        );
      case "CONFIRMED (PAY ON ARRIVAL)":
      case "CONFIRMED":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <Check className="w-3 h-3" />
            Confirmed
          </span>
        );
      case "CANCELLED":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <X className="w-3 h-3" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
            {status || "Unknown"}
          </span>
        );
    }
  };

  const pendingQuotes = filteredQuotes.filter(
    (q) => q.status === "PENDING" || q.status?.toUpperCase() === "PENDING"
  );
  const otherQuotes = filteredQuotes.filter(
    (q) => q.status !== "PENDING" && q.status?.toUpperCase() !== "PENDING"
  );

  return (
    <div data-testid="admin-quotes">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">
            Quote Management
          </h1>
          <p className="text-slate-600">
            Review and confirm custom location quote requests
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full font-medium">
            <AlertCircle className="w-4 h-4" />
            {pendingQuotes.length} Pending
          </span>
        </div>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by booking ID, customer name, email, or location..."
              className="pl-10"
              data-testid="quote-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* Pending Quotes Section */}
      {pendingQuotes.length > 0 && (
        <div className="mb-8">
          <h2 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            Pending Quotes ({pendingQuotes.length})
          </h2>
          <div className="grid gap-4">
            {pendingQuotes.map((quote) => (
              <motion.div
                key={quote.booking_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="border-l-4 border-l-amber-500">
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      {/* Quote Info */}
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-primary-600">
                            {quote.booking_id}
                          </span>
                          {getStatusBadge(quote.status)}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-2 text-slate-600">
                            <User className="w-4 h-4" />
                            {quote.customer_name}
                          </div>
                          <div className="flex items-center gap-2 text-slate-600">
                            <Mail className="w-4 h-4" />
                            {quote.customer_email}
                          </div>
                          <div className="flex items-center gap-2 text-slate-600">
                            <Phone className="w-4 h-4" />
                            {quote.customer_phone}
                          </div>
                          <div className="flex items-center gap-2 text-slate-600">
                            <Clock className="w-4 h-4" />
                            {new Date(quote.pickup_datetime).toLocaleString()}
                          </div>
                        </div>
                        <div className="flex items-start gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700">
                            <span className="font-medium">
                              {quote.pickup_location}
                            </span>
                            <span className="text-slate-400 mx-2">→</span>
                            <span className="font-medium">
                              {quote.dropoff_location}
                            </span>
                          </span>
                        </div>
                        <div className="text-sm text-slate-500">
                          {quote.passengers} passenger{quote.passengers !== 1 && "s"} •{" "}
                          {quote.trip_type}
                        </div>
                        {quote.notes && (
                          <div className="text-sm text-slate-600 bg-slate-50 p-2 rounded">
                            <strong>Notes:</strong> {quote.notes}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedQuote(quote)}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRejectQuote(quote.booking_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleOpenConfirm(quote)}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`confirm-quote-${quote.booking_id}`}
                        >
                          <DollarSign className="w-4 h-4 mr-1" />
                          Set Price
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* All Quote Requests (including confirmed/rejected) */}
      {otherQuotes.length > 0 && (
        <div>
          <h2 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-500" />
            Processed Quotes ({otherQuotes.length})
          </h2>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-sm text-slate-600">
                      <th className="px-6 py-4 font-semibold">Booking ID</th>
                      <th className="px-6 py-4 font-semibold">Customer</th>
                      <th className="px-6 py-4 font-semibold">Route</th>
                      <th className="px-6 py-4 font-semibold">Date</th>
                      <th className="px-6 py-4 font-semibold">Price</th>
                      <th className="px-6 py-4 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {otherQuotes.map((quote) => (
                      <tr
                        key={quote.booking_id}
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => setSelectedQuote(quote)}
                      >
                        <td className="px-6 py-4 font-medium text-primary-600">
                          {quote.booking_id}
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-slate-900">
                              {quote.customer_name}
                            </p>
                            <p className="text-sm text-slate-500">
                              {quote.customer_email}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-600 text-sm">
                          <div className="max-w-[200px] truncate">
                            {quote.pickup_location} → {quote.dropoff_location}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-600 text-sm">
                          {new Date(quote.pickup_datetime).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 font-semibold text-slate-900">
                          €{quote.total_price || 0}
                        </td>
                        <td className="px-6 py-4">{getStatusBadge(quote.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empty State */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading quotes...</div>
      ) : filteredQuotes.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-semibold text-slate-700 mb-2">No Quote Requests</h3>
            <p className="text-slate-500">
              Quote requests appear when customers select "Other Location" during booking.
            </p>
          </CardContent>
        </Card>
      ) : null}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-slate-600">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Quote Details Modal */}
      <Dialog open={!!selectedQuote} onOpenChange={() => setSelectedQuote(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Quote Details - {selectedQuote?.booking_id}
            </DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-primary-600" />
                    Customer Info
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-slate-500">Name:</span>{" "}
                      {selectedQuote.customer_name}
                    </p>
                    <p>
                      <span className="text-slate-500">Email:</span>{" "}
                      {selectedQuote.customer_email}
                    </p>
                    <p>
                      <span className="text-slate-500">Phone:</span>{" "}
                      {selectedQuote.customer_phone}
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary-600" />
                    Route Info
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-slate-500">From:</span>{" "}
                      {selectedQuote.pickup_location}
                    </p>
                    <p>
                      <span className="text-slate-500">To:</span>{" "}
                      {selectedQuote.dropoff_location}
                    </p>
                    <p>
                      <span className="text-slate-500">Passengers:</span>{" "}
                      {selectedQuote.passengers}
                    </p>
                    <p>
                      <span className="text-slate-500">Trip Type:</span>{" "}
                      {selectedQuote.trip_type}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary-600" />
                  Schedule
                </h3>
                <p className="text-sm">
                  {new Date(selectedQuote.pickup_datetime).toLocaleString()}
                </p>
              </div>

              {selectedQuote.notes && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">
                    Customer Notes
                  </h3>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                    {selectedQuote.notes}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t">
                <div>
                  <span className="text-slate-500">Status:</span>{" "}
                  {getStatusBadge(selectedQuote.status)}
                </div>
                {selectedQuote.total_price > 0 && (
                  <div className="text-right">
                    <span className="text-slate-500">Confirmed Price:</span>{" "}
                    <span className="font-heading font-bold text-xl text-primary-600">
                      €{selectedQuote.total_price}
                    </span>
                  </div>
                )}
              </div>

              {selectedQuote.status === "PENDING" && (
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => handleRejectQuote(selectedQuote.booking_id)}
                    className="text-red-600"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    onClick={() => {
                      setSelectedQuote(null);
                      handleOpenConfirm(selectedQuote);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Set Price & Confirm
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Quote Modal */}
      <Dialog
        open={!!confirmingQuote}
        onOpenChange={() => setConfirmingQuote(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Confirm Quote
            </DialogTitle>
            <DialogDescription>
              Set the price for booking {confirmingQuote?.booking_id}
            </DialogDescription>
          </DialogHeader>
          {confirmingQuote && (
            <div className="space-y-6 mt-4">
              {/* Route Summary */}
              <div className="bg-slate-50 p-4 rounded-lg text-sm">
                <p className="font-medium text-slate-900 mb-2">Route Summary</p>
                <p className="text-slate-600">
                  {confirmingQuote.pickup_location} → {confirmingQuote.dropoff_location}
                </p>
                <p className="text-slate-500 mt-1">
                  {confirmingQuote.passengers} passenger
                  {confirmingQuote.passengers !== 1 && "s"} •{" "}
                  {confirmingQuote.trip_type}
                </p>
              </div>

              {/* Price Input */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Quote Price (EUR) *
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                    €
                  </span>
                  <Input
                    type="number"
                    min="1"
                    step="0.01"
                    value={quotePrice}
                    onChange={(e) => setQuotePrice(e.target.value)}
                    placeholder="Enter price..."
                    className="pl-8"
                    data-testid="quote-price-input"
                    autoFocus
                  />
                </div>
              </div>

              {/* Admin Notes */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Admin Notes (Optional)
                </Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Internal notes about this quote..."
                  rows={2}
                />
              </div>

              {/* Send Email Option */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="send-email"
                  checked={sendEmail}
                  onCheckedChange={setSendEmail}
                />
                <Label htmlFor="send-email" className="text-sm cursor-pointer">
                  Send confirmation email to customer
                </Label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setConfirmingQuote(null)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmQuote}
                  disabled={isSubmitting || !quotePrice}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="submit-quote-btn"
                >
                  {isSubmitting ? (
                    "Confirming..."
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Confirm Quote
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuotes;
